'use strict';

/* Module dependencies */
const _ = require('lodash');
const Promise = require('bluebird');
const Cookie = require('js-cookie');
const EventEmitter = require('wolfy87-eventemitter');

const Socket = require('./Connection/Socket');

/* @private User session token, not accessible through any public method */
let _token;


/**
 * Handles all event-related stuff and cookie management in the browser.
 */
class Connection extends EventEmitter {
    /**
     * Initializes private attributes to default values
     * @return {Connection} A new Connection instance
     */
    constructor () {
        super();
        this.listeningEvents = [];
        this.cookieName = 'i.cc';
        this._options = { socket: {}, isAgent: false };
        this._ping = 1;
        this.timeDifferenceWithServer = 0;
        this.taskId = '';
    }

    /**
     * Set the connection configuration
     * @param {String} appId   The ID of the app that is connecting
     * @param {String} server  Server url to which the connection will be opened
     * @param {Object} options Extra connection options
     * @param {String} type    The connection type (socket by default)
     * @param {Object} api
     */
    configure (appId, server, options = {}, type = 'socket', api) {
        this._appId = appId;
        this._server = server;
        this._type = type;
        this._options[this._type] = options;
        if (options.isAgent) {
            this._options.isAgent = options.isAgent;
        }
        this.api = api;
    }

    /**
     * Opens a new connection with the server.
     * The connection protocol will depend on the set type.
     * @param {Object} dataIn    The necessary data to open a connection. This is, user ID and session token
     * @return {Promise<Object|Error>}   A Promise fulfilled with all user data
     */
    open (dataIn, retrying = false, onReopenCallback) {
        if (this._conn && this.me()) {
            // If it's already open, just return the same data is it were being opened
            return Promise.resolve(this.me());
        }
        // load data from cookie if it's not set
        const data = ((_.isObjectLike(dataIn) && !_.isEmpty(dataIn)) ? dataIn : this.getCookie());
        return Promise.resolve()
            .then(() => {
                if (!this._type || !data) {
                    return this._handleConnectionError('Missing data for opening a connection');
                }
                if (!data.id) {
                    return this._handleConnectionError('Missing id');
                }
                if (!data.token) {
                    return this._handleConnectionError('Missing token');
                }

                // set private token variable
                _token = data.token;
                // if invitations property is not set, defaults to true
                if (_.isUndefined(data.invitations)) {
                    data.invitations = true;
                }
                this._options[this._type].invitations = data.invitations;

                return data.id;  // return userId
            })
            .then(userId => {
                // Get a one use only token to authenticate the socket handshake request
                return this.getHandshakeToken()
                    .then(handshakeToken => {
                        return { tokenName: 'hsk', token: handshakeToken, userId};
                    })
                    .catch(() => {
                        return { tokenName: 'token', token: this.getToken(), userId };
                    });
            })
            .then(({ tokenName, token, userId }) => {
                this._openConnection(tokenName, token);
                if (!this._conn) {
                    return this._handleConnectionError('Opening connection failed');
                }

                const Perror = this.onError()
                    .then((err) => {
                        // if the error is because of 'session id unknown' (no cookies)
                        // the error format will be { type: 'TransportError', description: 400 }
                        // just stop reconnecting and throw an error
                        if (err && err.type === 'TransportError' && err.description === 400) {
                            this.close();
                            err.code = err.description;
                            throw err;
                        } else if (err === 'Token is invalid' && !retrying) {
                            this.removeCookie();
                            _token = null;
                            this._conn = false;
                            return this.open(dataIn, true, onReopenCallback)
                                .then(() => {
                                    onReopenCallback();
                                });
                        } else if (err === 'Handshake token is invalid') {
                            this._conn = false;
                            return this.open(dataIn, true, onReopenCallback)
                                .then(() => {
                                    onReopenCallback();
                                });
                        }
                        // keep the old code to avoid breaking something
                        throw new Error(err);
                    });

                const Psuccess = this.onConnect()
                    .then((connectData) => {
                        // change the token in the query string to avoid requesting
                        // another hsk in case there is a reconnection
                        this._conn.replaceQueryStringToken('token', this.getToken());

                        this.initialPingTime = Date.now();
                        this._conn._emitEvent('initial-ping');

                        const { getUnixTime } = require('./Helper');
                        const serverTime = connectData.serverTime;
                        if (serverTime && Number.isInteger(serverTime) && serverTime > 0) {
                            this.timeDifferenceWithServer = getUnixTime() - serverTime;
                        }
                        if (connectData.taskId) {
                            this.taskId = connectData.taskId;
                        }
                        // If user was offline in server, remove his/her cookie
                        // to prevent errors during the loading process.
                        connectData.userWasOffline && this.removeCookie();
                        return this.initUserSession(userId, data.rooms);
                    });

                // on reconnection, rebuild user session
                this.setReconnectionProtocol();

                return Promise.race([Psuccess, Perror]);
            });
    }

    /**
     * Join to specified rooms, get user data and active chats and invitations
     *
     * @param  {string} userId
     * @param  {array}  rooms  Room ids array
     * @return {Promise}
     */
    initUserSession (userId, rooms) {
        let joinedRooms = rooms;
        return this.joinRooms(userId, rooms)
            .catch(() => [])  // Some error joining rooms? Just move on with no rooms
            .then((joined) => {
                joinedRooms = joined;
                return this.getUserData(userId);
            })
            .then((userData) => {
                this._user = userData;
                // set actual rooms
                this._user.rooms = joinedRooms;
                return this.getUserChatsAndInvites();
            })
            .then(() => this.storeSessionCookie())
            .then(() => this._user);
    }

    getCookieName () {
        return this._options.isAgent ? 'agent-' + this.cookieName : this.cookieName;
    }

    // Get a one use only token to authenticate the socket handshake request
    getHandshakeToken () {
        return this.api.request('/handshake', 'POST', {
            'appId': this.getApp(),
            'token': this.getToken()
        })
            .then(({ data: handshakeData }) => {
                return handshakeData.hsk;
            });
    }

    /**
     * Set the steps to follow after reconnecting
     */
    setReconnectionProtocol () {
        return this.onReconnect()
            .then((attempt) => {
                // emit a reconnect event to the server to handle the reconnection properly
                this.send({
                    name: 'reconnection',
                    data: { attempt },
                });
                const cookieData = this.getCookie();
                if (cookieData) {
                    return this.initUserSession(cookieData.id, cookieData.rooms);
                } else {
                    return null;
                }
            });
    }

    /**
     * Close the connection to the server and remove the cookie from the browser
     */
    close () {
        // disconnect from server
        if (this._conn) {
            this._conn.disconnect();
        }
        this._conn = false;
        this.listeningEvents = [];
        // delete user cookie
        this.removeCookie();
    }

    /**
     * Sends an event to the server using the actual connection
     * @param  {Event}  event     Event object
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    send (event) {
        if (!this._conn) {
            return this._handleConnectionError();
        }

        return this._conn.send(event.name, event.data);
    }

    /**
     * Uploads a media file to the server
     * @param  {Object} data       Contains media (the resource) and info (metadata)
     * @param  {Function} onProgress Callback to run while file is being uploaded
     * @return {Promise<Object>}    A Promise fulfilled with the server response
     */
    upload (data, onProgress) {
        if (!this._conn) {
            return this._handleConnectionError();
        }

        return this._conn.upload(data, onProgress);
    }

    /**
     * Listen for a specific server event
     * @param  {String} eventName Event name
     * @param {Function} callback callback to run when event is fired
     */
    listen (eventName, callback) {
        if (!this._conn) {
            throw new Error('No connection open');
        }
        if (this.listeningEvents.indexOf(eventName) === -1) {
            this.listeningEvents.push(eventName);
        }

        return this._conn.on(eventName, callback);
    }

    /**
     * Listen for a specific server event and return a promise
     * @param  {String} eventName Event name
     */
    listenPromise (eventName) {
        if (!this._conn) {
            return this._handleConnectionError();
        }
        if (this.listeningEvents.indexOf(eventName) === -1) {
            this.listeningEvents.push(eventName);
        }

        return new Promise((resolve) => this._conn.on(eventName, resolve));
    }

    /**
     * Removes the listener of the specified event
     * @param  {String}   eventName Event name
     * @param  {Function} listener  Listener attached to the event,
     *                              necessary for not removing all listeners with the same name
     */
    removeListener (eventName, listener) {
        super.removeListener(eventName, listener);
        const index = this.listeningEvents.indexOf(eventName);
        if (index !== -1) {
            this.listeningEvents.splice(index, 1);
        }
        return this._conn.removeListener(eventName, listener);
    }

    /**
     * Checks whether the token cookie is set
     * @return {Boolean}
     */
    isCookieSet () {
        return !!(Cookie.get(this.getCookieName()));
    }

    /**
     * Retrieve the connection coookie
     * @return {Object} Cookie data (userId, token and rooms connected)
     */
    getCookie () {
        if (this.isCookieSet()) {
            return JSON.parse(decodeURIComponent(Cookie.get(this.getCookieName())));
        }
        return null;
    }

    /**
     * Set the connection cookie with the given data
     * @param {Object}   data   Data object to store in the cookie
     */
    setCookie (data) {
        Cookie.set(
            this.getCookieName(),
            encodeURIComponent(JSON.stringify(data)),
            this.getCookieOptions()
        );
    }

    /**
     * Update the connection cookie with the given data
     * @param {Object}   data   Data object to store in the cookie
     */
    updateCookie (data={}) {
        const cookie    = this.getCookie();
        const newCookie = cookie ? _.extend(cookie, data) : data;
        this.setCookie(newCookie);
    }

    /**
     * Removes the connection cookie
     */
    removeCookie () {
        Cookie.remove(this.getCookieName(), this.getCookieOptions());
    }

    /**
     * Get the cookie options
     *
     * @return {object}
     */
    getCookieOptions () {
        const options = {
            secure: true
        };

        if (this.getOptions().setCookieOnDomain === true) {
            options.domain = '.'+this.getDomainWithoutSubdomain();
        }
        const sameSiteValues = ["None", "Strict", "Lax"];
        if (this.getOptions().setCookieSameSite && sameSiteValues.includes(this.getOptions().setCookieSameSite)) {
            options.sameSite = this.getOptions().setCookieSameSite;
        }

        return options;
    }

    /**
     * Retrieves the private connection information
     * @return {Socket} Connection information
     */
    getConnection () {
        return this._conn || false;
    }

    /**
     * Set a new parameter to the connection object
     * @param  {String} key   The parameter's key
     * @param  {*}  value     The parameter's value
     * @return {Object}       Connection information
     */
    setConnectionParameter (key, value) {
        if (this._conn) {
            this._conn[key] = value;
            return this._conn;
        }
        return false;
    }

    /**
     * Retrieves the connection holder's information
     * @return {Object} User information
     */
    me () {
        return this._user || {};
    }

    /**
     * Retrieves the user session access token
     * @return {String} User session access token
     */
    getToken () {
        return _token || false;
    }

    removeToken () {
        _token = false;
    }

    /**
     * Retrieves the appId to which the connection is connected to
     * @return {String} App's ID
     */
    getApp () {
        return this._appId;
    }

    /**
     * Get the options for the specified type of connection
     * @param  {String} type Connection type
     * @return {Object}      Options object
     */
    getOptions (type = null) {
        return this._options[type || this._type] || {};
    }

    /**
     * Retrieves the server to which the connection is established
     * @return {String} Server's name
     */
    getServer () {
        return this._server;
    }

    /**
     * Retrieves the connection type
     * @return {String} type Connection type
     */
    getType () {
        return this._type;
    }

    get ping () {
        return this._ping;
    }

    set ping (ms) {
        this._ping = ms;
        this.emit('ping-update', ms);
    }

    /**
     * Register a callback to run when connection is up, or return a promise
     *
     * @param  {Function} callback (optional) Callback function to run
     */
    onConnect (callback = null) {
        if (callback) {
            this.listen('connection:success', callback);
        } else {
            return this.listenPromise('connection:success');
        }
    }

    /**
     * Register a callback to run when connection is down, or return a promise
     *
     * @param  {Function} callback (optional) Callback function to run
     */
    onDisconnect (callback = null) {
        if (callback) {
            this.listen('disconnect', callback);
        } else {
            return this.listenPromise('disconnect');
        }
    }

    /**
     * Register a callback to run when there has been a reconnection, or return a promise
     *
     * @param  {Function} callback (optional) Callback function to run
     */
    onReconnect (callback = null) {
        if (callback) {
            this.listen('reconnect', callback);
        } else {
            return this.listenPromise('reconnect');
        }
    }

    /**
     * Register a callback to run when an error has happened, or return a promise
     *
     * @param  {Function} callback (optional) Callback function to run
     */
    onError (callback = null) {
        if (callback && typeof callback === 'function') {
            let called = false;
            const cb = () => {
                if (!called) {
                    called = true;
                    return callback();
                }
            };
            this.listen('connection:error', cb);
            this.listen('error', cb);
        } else {
            return Promise.race([
                // socket will emit an 'error' if there are problems during the token and app validations
                // before the connection is established
                this.listenPromise('error'),
                // socket will emit a 'connection:error' if there are problems during the connection logic
                // after the connection is established
                this.listenPromise('connection:error')
            ]);
        }
    }

    /**
     * Open connection of the specified type
     *
     * @param  {string} tokenName   Parameter name for the passed JWT. Can be 'token' or 'hsk'
     * @param  {string} token       JWT to authenticate the handshake request
     * @return {Object} Connection object
     */
    _openConnection (tokenName, token) {
        if (!token) {
            throw new Error('missing token');
        }

        switch (this._type) {
        case 'socket':
            this._conn = new Socket(
                this._server,
                this.getApp(),
                tokenName,
                token,
                this.getOptions()
            );
            break;
        }
        return this._conn;
    }

    /**
     * Join the specified rooms
     *
     * @param {array} rooms Room ids array
     * @return {Promise}
     */
    joinRooms (userId, rooms) {
        if (rooms && rooms.length) {
            return this.send({
                name: 'rooms:join',
                data: { roomIds: rooms, userId: userId }
            })
                .then(res => res.joinedRooms);
        }
        return Promise.resolve([]);
    }

    /**
     * Get the user data from the server
     *
     * @param  {string} userId
     * @return {Promise}
     */
    getUserData (userId) {
        // load user data from server via a 'users:get'
        return this.send({
            name: 'users:get',
            data: { userId: userId }
        })
            .then((res) => res.user);
    }

    /**
     * Get user chats and invitations from the server
     *
     * @param  {string} userId  Optional
     * @return {Promise}
     */
    getUserChatsAndInvites (userId = null) {
        const getChatsAndInvites = () => {
            if (this._user.chats || this._user.chatsWatching || this._user.invitations) {
                // retrieve this user's active chats as well if needed via a 'users:chats'
                return this.send({
                    name: 'users:chats',
                    data: { userId: this._user.id }
                })
                    .then((res) => {
                        this._user.chats = res.chats;
                        this._user.chatsWatching = res.chatsWatching;

                        this._user.invitations = {};
                        for (const key in res.invitations) {
                            this._user.invitations[key] = res.invitations[key];
                            if (res.invitations[key].invitationExpiration) {
                                const { getBrowserExpirationTime } = require('./Helper');
                                this._user.invitations[key].invitationExpiration = getBrowserExpirationTime(res.invitations[key].invitationExpiration, this.timeDifferenceWithServer, this.ping);
                            }
                        }

                        return this._user;
                    });
            } else {
                return this._user;
            }
        };

        if (userId && (!this._user || _.isEmpty(this._user))) {
            return this.getUserData(userId)
                .then(() => getChatsAndInvites());
        } else {
            return getChatsAndInvites();
        }
    }

    /**
     * Store the user's session cookie.
     * It only sets it if the user hadn't a previous session, to avoid overwriting it
     */
    storeSessionCookie () {
        // set token in a cookie to be able to reload the session
        const hadPreviousSession = this.isCookieSet();
        if (!hadPreviousSession) {
            const invitations = this.getOptions().invitations;
            const invitationsDefined = typeof invitations !== 'undefined' && invitations !== null;
            this.setCookie({
                token: this.getToken(),
                id: this._user.id,
                appId: this._appId,
                rooms: this._user.rooms || [],
                invitations: invitationsDefined ? invitations : true
            });
        }
    }

    /**
     * Handle any error regarding this class that needs to reject
     * @param {String} errorMessage Error message to be sent back
     */
    _handleConnectionError (errorMessage = 'No connection open') {
        return Promise.reject({
            message: errorMessage,
            type: 'connection'
        });
    }

    /**
     * Get the domain name without the subdomain
     * E.g: foo.bar.com -> bar.com
     *
     * @return {string}
     */
    getDomainWithoutSubdomain () {
        // get the hostname and split it into dots
        let hostnameParts = document.location.hostname.split('.');
        // get rid of the subdomain part
        hostnameParts.shift();
        // join the remaining parts
        return hostnameParts.join('.');
    }
}

module.exports = new Connection();



// WEBPACK FOOTER //
// ./src/Connection.js